import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { useSnackBar } from '../../../../contexts/snackbar';
import {
  RecurrentExpense,
  RecurrentExpenseNature,
} from '../../../../interfaces/delivery-company/recurrent-expenses';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import { GenericList } from '../../../common/generic-list/generic-list';
import {
  Replay,
  AssignmentLate,
  Label,
  Check,
  Cancel,
  CancelOutlined,
  Start,
} from '@mui/icons-material';
import {
  Button,
  Chip,
  CircularProgress,
  Switch,
  TablePagination,
} from '@mui/material';
import { format } from 'date-fns';

import {
  Expense,
  ExpenseStatus,
} from '../../../../interfaces/delivery-company/expenses';
import { appendToPaginatedItems } from '../../../../utils/append_to_paginated_items';
import { displayMoney } from '../../../../utils/money';
import { IconWithText } from '../../../common/styles/components';
import { showExpenseState } from '../../../common/styles/expenses.styles';
import ExpenseFormDialog from '../expenses/expense-form-dialog';
import expenses from '../expenses/expenses';
import ManageCategories from '../expenses/manage-labels';
import RecurrentExpenseFormDialog from './expense-form-dialog';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { blue, red } from '@mui/material/colors';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';

function RecurringExpenses() {
  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [recurringExpenses, setRecurringExpenses] =
    useState<RecurrentExpense[]>();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [managingLabels, setManagingLabels] = useState(false);
  const [modifyintRecurringExpense, setModifyingRecurrentExpense] =
    useState<RecurrentExpense | null>(null);

  const [includeDeactivated, setIncludeDeactivated] = useState(false);

  const [pendingActivationChange, setPendingActivationChange] = useState(0);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const fetchRecurringExpenses = async () => {
    setIsLoading(true);
    try {
      let endpoint = `${API_ENDPOINT}/v1/recurrent-payments/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&nature=${RecurrentExpenseNature.EXPENSE}`;

      if (includeDeactivated) endpoint += `&include_deactivated=true`;

      const response = await axios.get<{
        count: number;
        results: RecurrentExpense[];
      }>(endpoint, config);
      setRecurringExpenses(response.data.results);
      setCount(response.data.count);
    } catch {
      showMessage('Failed to get recurrent expenses', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecurringExpenses();
  }, [includeDeactivated, JSON.stringify(controller)]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const toggleActivation = async (recurrentExpense: RecurrentExpense) => {
    setPendingActivationChange(recurrentExpense.id);
    try {
      const endpoint = `${API_ENDPOINT}/v1/recurrent-payments/${recurrentExpense.id}/${recurrentExpense.is_active ? 'deactivate' : 'activate'}/`;
      await axios.post(endpoint, {}, config);
      showMessage(
        `Label ${recurrentExpense.name} ${recurrentExpense.is_active ? 'deactivated' : 'activated'} successfully.`
      );
      if (
        (recurrentExpense.is_active && includeDeactivated) ||
        !recurrentExpense.is_active
      )
        setRecurringExpenses(
          recurringExpenses!.map((old) =>
            old.id == recurrentExpense.id
              ? { ...recurrentExpense, is_active: !recurrentExpense.is_active }
              : old
          )
        );
      else {
        setRecurringExpenses(
          recurringExpenses!.filter((item) => item.id != recurrentExpense.id)
        );
      }
    } catch {
      showMessage(
        `Failed to  ${recurrentExpense.is_active ? 'deactivate' : 'activate'} ${recurrentExpense.name}.`,
        'error'
      );
    } finally {
      setPendingActivationChange(0);
    }
  };

  return (
    <div>
      <div
        style={{
          width: '100%',
          gap: 6,
          justifyContent: 'space-between',
          display: 'flex',
        }}
      >
        <Button variant="contained" 
          disabled={!user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS, ResourceActionType.CREATE)}
          onClick={() => setAddingNew(true)}>
          New Recurring Payment
        </Button>

        <div style={{ display: 'flex', gap: 7 }}>
          <Button
            sx={{ pl: 1 }}
            variant="outlined"
            disabled={!user_has_data_model_access(user, DataResources.EXPENSE_CATEGORIES, ResourceActionType.VIEW)}
            onClick={() => setManagingLabels(true)}
          >
            <Label fontSize="small" sx={{ mr: 1 }} /> Expense Categories
          </Button>
        </div>
      </div>
      <br />
      <Switch
        checked={includeDeactivated}
        onChange={() => setIncludeDeactivated(!includeDeactivated)}
      />{' '}
      Show deactivated payments
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Chip
          color="primary"
          label={`${count} Recurrent Payments`}
          variant={'outlined'}
        />
      </div>
      <br></br>
      <GenericList<RecurrentExpense>
        sourceView="recurring-expenses"
        items={recurringExpenses ?? []}
        selectedItems={new Map()}
        isLoading={isLoading}
        fullWidth
        columns={[
          {
            id: 'id',
            header: 'ID',
            display_body: (expense) => (
              <>
                <div
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => setModifyingRecurrentExpense(expense)}
                >
                  <b>{expense.id}</b>
                </div>

                <div style={{ marginTop: '2px' }}>
                  {format(expense.created_at!, 'dd-MM-yyyy HH:mm')}
                </div>
              </>
            ),
          },
          {
            id: 'name',
            header: 'Name',
            display_body: (expense) => <Chip label={expense.name} />,
          },
          {
            id: 'label',
            header: 'Category',
            display_body: (expense) => <Chip label={expense.category!.label} />,
          },
          {
            id: 'amount',
            header: 'Amount',
            display_body: (expense) => <>{displayMoney(expense.expense)}</>,
          },
          {
            id: 'period',
            header: 'Period',
            display_body: (expense) => (
              <>
                Since{' '}
                {expense.start_date && format(expense.start_date, 'dd-MM-yyyy')}{' '}
                {expense.end_date && (
                  <>
                    <br /> until {format(expense.end_date, 'dd-MM-yyyy')}
                  </>
                )}
              </>
            ),
          },
          {
            id: 'due',
            header: 'Next Due Date',
            display_body: (expense) => (
              <>
                {expense.next_due_date &&
                  format(expense.next_due_date, 'dd-MM-yyyy')}
              </>
            ),
          },
          {
            id: 'schedule',
            header: 'Schedule',
            display_body: (item) => item.frequency,
          },
          {
            id: 'active',
            header: 'Active',
            display_body: (item) =>
              item.is_active ? (
                <Check color="success" />
              ) : (
                <Cancel color="error" />
              ),
          },
          {
            id: 'actions',
            header: 'Actions',
            display_body: (recurrentExpense) => (
              <>
                {recurrentExpense.is_active ? (
                  <Chip
                    icon={
                      pendingActivationChange == recurrentExpense.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CancelOutlined fontSize="small" />
                      )
                    }
                    label={'Deactivate'}
                    disabled={pendingActivationChange > 0 || !user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS, ResourceActionType.UPDATE)}
                    onClick={() => toggleActivation(recurrentExpense)}
                    style={{ background: red[100], cursor: 'pointer' }}
                  />
                ) : (
                  <Chip
                    icon={
                      pendingActivationChange == recurrentExpense.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Start fontSize="small" />
                      )
                    }
                    label={'Reactivate'}
                    disabled={pendingActivationChange > 0 || !user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS, ResourceActionType.UPDATE)}
                    style={{ background: blue[100] }}
                    onClick={() => toggleActivation(recurrentExpense)}
                  />
                )}
              </>
            ),
          },
        ]}
        getItemUID={(expense) => expense.id.toString()}
        rowConfigs={[]}
      ></GenericList>
      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={count}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
      {addingNew && recurringExpenses && (
        <RecurrentExpenseFormDialog
          onSuccess={(e) => {
            setRecurringExpenses(
              appendToPaginatedItems(10, recurringExpenses, e)
            );
          }}
          onClose={() => setAddingNew(false)}
          open={addingNew}
        />
      )}
      {modifyintRecurringExpense && (
        <RecurrentExpenseFormDialog
          onClose={() => setModifyingRecurrentExpense(null)}
          defaultRecurrentExpense={modifyintRecurringExpense}
          onSuccess={(e) => {
            setRecurringExpenses(
              recurringExpenses!.map((item) => (item.id == e.id ? e : item))
            );
          }}
          open={modifyintRecurringExpense != null}
        />
      )}
      {managingLabels && (
        <ManageCategories onClose={() => setManagingLabels(false)} />
      )}
    </div>
  );
}

export default RecurringExpenses;
